/**
 * K108-R Batch 4 — launched/monitored training session history.
 *
 * Reads the technician's training_consumption_sessions (INCLUDING pending /
 * in-progress ones — unlike useAllConsumptionSessions, which is completed-only)
 * with the artifact joined for title/provider. RLS already permits a technician
 * to read their own sessions. NO DB change.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { HistorySessionInput } from '@/lib/training/trainingHistoryCard';

export function useTrainingSessionHistory(technicianProfileId: string | null | undefined, limit = 50) {
  return useQuery<HistorySessionInput[]>({
    queryKey: ['training-session-history', technicianProfileId, limit],
    enabled: !!technicianProfileId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(
          'id, artifact_id, ticket_id, completion_status, opened_at, updated_at, confirmed_at, opened_url_snapshot, expected_duration_minutes, metadata, artifact:training_artifacts(id, title, provider_name, artifact_type), ticket:tickets(id, simple_ticket_number, ticket_number)',
        )
        .eq('technician_profile_id', technicianProfileId as string)
        // K108-Z2f: superseded attempts (a newer launch replaced this one) are an
        // internal lifecycle state, not user-facing history — hide them so old
        // replaced attempts never appear as active/resumable partials.
        .neq('completion_status', 'superseded')
        .order('opened_at', { ascending: false })
        .limit(limit);
      if (error) return [];
      const rows = (data ?? []) as unknown as HistorySessionInput[];
      // Core-100 R4 CORE-O: the catalog's RLS hides retired training, so the embedded artifact comes back null for
      // it. The person's OWN history still names it and says it is retired (my_training_history_artifacts).
      const { data: hist } = await supabase.rpc('my_training_history_artifacts' as never, {
        p_technician_profile_id: technicianProfileId,
      } as never);
      const byId = new Map(
        (((hist ?? []) as unknown) as Array<{ artifact_id: string; title: string; provider_name: string | null; artifact_type: string | null; is_active: boolean }>)
          .map((a) => [a.artifact_id, a]),
      );
      return rows.map((r) => {
        const sessionArtifactId = (r as unknown as { artifact_id?: string }).artifact_id ?? r.artifact?.id;
        const h = sessionArtifactId ? byId.get(sessionArtifactId) : undefined;
        if (!h) return r.artifact ? { ...r, artifact: { ...r.artifact, is_active: r.artifact.is_active ?? true } } : r;
        return {
          ...r,
          artifact: {
            id: h.artifact_id,
            title: r.artifact?.title ?? h.title,
            provider_name: r.artifact?.provider_name ?? h.provider_name,
            artifact_type: r.artifact?.artifact_type ?? h.artifact_type,
            is_active: h.is_active,
          },
        };
      });
    },
  });
}
